'use client';

import { type JSX, useState } from 'react';
import { translate } from '@cfixe-monorepo/shared';
import { revealProtectedBindings } from '../../../website/api/professional-api';

/**
 * Remplacement du tunnel quand la réservation en ligne est suspendue (semi-gel dunning J+30
 * ou abo gelé). Message NEUTRE : la raison n'est JAMAIS publique — afficher un impayé sur la
 * vitrine d'un pro serait diffamatoire.
 *
 * Partagé par TOUS les tunnels (widget site custom, `/book/[slug]`, widget embarqué) : le flag
 * `online_booking_suspended` vient du même profil public, la carte de remplacement doit venir
 * du même composant. Le refus dur reste côté API (`createPreReservation`).
 *
 * Le numéro n'arrive pas avec le profil : `pro:phone` est un binding protégé (masqué en SSR,
 * anti-scraping) — on le récupère au clic via l'endpoint captcha `reveal-bindings`, exactement
 * comme ContactReveal sur le reste du site. Deuxième clic = appel réel (`tel:`).
 *
 * `getCaptchaToken` est injecté par l'hôte plutôt que résolu ici : le widget builder résout son
 * token depuis son propre `capRef`, frontend-web depuis son adaptateur `@/lib/captcha` lié à sa
 * base API proxifiée. Un capRef en dur ici casserait l'un des deux.
 */
export function BookingSuspendedCard({
  professionalSlug,
  getCaptchaToken,
  className,
}: {
  professionalSlug: string;
  getCaptchaToken: () => Promise<string | null>;
  /** Classes de l'hôte (ex. `g_margin builder-booking-widget` côté builder). */
  className?: string;
}): JSX.Element {
  const [phone, setPhone] = useState<string | null>(null);
  const [isRevealing, setIsRevealing] = useState(false);
  const label = (key: string): string => translate('fr-FR', 'BOOKING_SUSPENDED_CARD', key) ?? key;

  const reveal = async (): Promise<void> => {
    if (phone || isRevealing) return;
    setIsRevealing(true);
    try {
      const token = await getCaptchaToken();
      if (!token) return;
      const values = await revealProtectedBindings(professionalSlug, ['pro:phone'], token);
      setPhone(values['pro:phone'] || null);
    } catch {
      // Silencieux : le bouton reste sur son libellé « voir le numéro », le visiteur peut réessayer.
    } finally {
      setIsRevealing(false);
    }
  };

  // Tokens du builder d'abord (`--color-*`, contexte site custom), puis ceux du tunnel
  // (`--bt-*-override` posés par /book, puis `--bt-*`), enfin un défaut neutre. Même chaîne de
  // repli que le reste des styles booking (cf. booking-manage-view.scss).
  const ctaStyle: React.CSSProperties = {
    display: 'inline-block',
    padding: '12px 24px',
    background: 'var(--color-cta, var(--bt-accent-override, var(--bt-accent, #2563eb)))',
    color: '#ffffff',
    border: 'none',
    cursor: 'pointer',
    fontSize: '1rem',
    fontFamily: 'inherit',
    borderRadius: 'var(--radius-global, var(--bt-radius-override, var(--bt-radius, 8px)))',
    textDecoration: 'none',
    fontWeight: 600,
  };

  return (
    <div className={className}>
      <div
        style={{
          background: 'var(--color-bg, var(--bt-bg-override, var(--bt-bg, #ffffff)))',
          color: 'var(--color-text, var(--bt-text-override, var(--bt-text, #111827)))',
          border:
            '1px solid color-mix(in srgb, var(--color-text, var(--bt-text-override, var(--bt-text, #111827))) 15%, transparent)',
          borderRadius: 'var(--radius-global, var(--bt-radius-override, var(--bt-radius, 8px)))',
          padding: '32px 24px',
          textAlign: 'center',
          fontFamily: 'var(--font-text, inherit)',
        }}
      >
        <h3 style={{ margin: '0 0 8px', fontSize: '1.25rem' }}>{label('TITLE')}</h3>
        <p style={{ margin: '0 0 20px', opacity: 0.8 }}>{label('DESC')}</p>
        {phone ? (
          <a href={`tel:${phone.replace(/[^\d+]/g, '')}`} style={ctaStyle}>
            {label('CALL_CTA')} : {phone}
          </a>
        ) : (
          <button type="button" onClick={(): void => void reveal()} disabled={isRevealing} style={ctaStyle}>
            {isRevealing ? label('REVEAL_CTA_LOADING') : label('REVEAL_CTA')}
          </button>
        )}
      </div>
    </div>
  );
}
